import { useState, useEffect } from 'react'
import styles from './index.module.scss'
import closeIcon from '../../../assets/icons/close.png'
import { Field } from '@Root/components'
import { API } from '@Root/API'
import { useDispatch } from 'react-redux'
import { snackbarActions } from '@Root/store'
import { errorMessage } from '@Root/helpers'

export const CreateNew = ({ onClose, shouldBeUpdated }) => {
  const dispatch = useDispatch()
  const [formData, setFormData] = useState({
    institution_id: null,
    form_name: '',
    cognito_form_id: '',
    form_type: 'application',
    description: '',
    api_key: '',
    is_active: true,
  })
  const [institutions, setInstitutions] = useState([])
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  const formTypes = [
    { label: 'Application Form', value: 'application' },
    { label: 'Update Form', value: 'update' },
  ]

  useEffect(() => {
    const fetchInstitutions = async () => {
      try {
        const { data } = await API.options.getInstitutions()
        setInstitutions(data.data.map(({ id, name }) => ({ value: id, label: name })))
      } catch (error) {
        console.error('Error fetching institutions:', error)
      }
    }
    fetchInstitutions()
  }, [])

  const onChangeValue = field => value => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }))
    }
  }

  const validate = () => {
    const newErrors = {}
    if (!formData.institution_id) {
      newErrors.institution_id = 'Institution is required'
    }
    if (!formData.form_name?.trim()) {
      newErrors.form_name = 'Form Name is required'
    }
    if (!formData.cognito_form_id?.trim()) {
      newErrors.cognito_form_id = 'Cognito Form ID is required'
    }
    if (!formData.form_type) {
      newErrors.form_type = 'Form Type is required'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) {
      return
    }

    setIsLoading(true)
    try {
      await API.cognitoForms.createForm(formData)
      dispatch(snackbarActions.setSuccessNotification({ text: 'Form created successfully' }))
      shouldBeUpdated?.()
      onClose?.()
    } catch (error) {
      dispatch(snackbarActions.setSnackbar({ text: errorMessage(error), isError: true }))
      setIsLoading(false)
    }
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h3 className={styles.headerText}>Create New Cognito Form</h3>
        <div className={styles.icon} onClick={onClose}>
          <img src={closeIcon} alt='' />
        </div>
      </div>
      <div className={styles.line} />
      <div className={styles.instructionWrapper}>
        <div className={styles.instruction}>
          <h3>Instruction</h3>
          <ol>
            <li>
              Go to the{' '}
              <a className={styles.link} href='https://www.cognitoforms.com' target='_blank' rel='noopener noreferrer'>
                https://www.cognitoforms.com
              </a>
              .
            </li>
            <li>Create a form with the required fields</li>
            <li>Get the form id</li>
            <li>
              Specify the webhook link{' '}
              <a className={styles.link} href='https://api.ctf.dev.redentu.com/api/cognito-webhook' target='_blank' rel='noopener noreferrer'>
                https://api.ctf.dev.redentu.com/api/cognito-webhook
              </a>
              where the Cognito form will be sent
            </li>
            <li>Create the record below, specifying the id of the Cognito form from which you will receive the data.</li>
          </ol>
        </div>
        <div className={styles.line} />
        <div className={styles.content}>
          <Field
            label='Institution'
            classNames={['transparent']}
            style={{ width: '100%' }}
            value={formData.institution_id}
            onChange={onChangeValue('institution_id')}
            placeholder='Select Institution'
            isDisabled={false}
            isRequired={true}
            field='select'
            options={institutions}
            error={errors.institution_id}
            styleWrapper={{ gridTemplateColumns: '200px 1fr' }}
          />

          <Field
            label='Form Name'
            classNames={['transparent']}
            style={{ width: '100%' }}
            value={formData.form_name}
            onChange={onChangeValue('form_name')}
            placeholder='Enter form name'
            isDisabled={false}
            isRequired={true}
            field='textField'
            error={errors.form_name}
            styleWrapper={{ gridTemplateColumns: '200px 1fr' }}
          />

          <div className={styles.fieldWrapper}>
            <Field
              label='Cognito Form ID'
              classNames={['transparent']}
              style={{ width: '100%' }}
              value={formData.cognito_form_id}
              onChange={onChangeValue('cognito_form_id')}
              placeholder='e.g., 4'
              isDisabled={false}
              isRequired={true}
              field='textField'
              error={errors.cognito_form_id}
              styleWrapper={{ gridTemplateColumns: '200px 1fr' }}
            />
            <div className={styles.helperText}>Form ID from Cognito Forms</div>
          </div>

          <Field
            label='Form Type'
            classNames={['transparent']}
            style={{ width: '100%' }}
            value={formData.form_type}
            onChange={onChangeValue('form_type')}
            placeholder='Select form type'
            isDisabled={false}
            isRequired={true}
            field='select'
            options={formTypes}
            error={errors.form_type}
            styleWrapper={{ gridTemplateColumns: '200px 1fr' }}
          />

          <Field
            label='Description'
            classNames={['transparent']}
            style={{ width: '100%', minHeight: '80px' }}
            value={formData.description}
            onChange={onChangeValue('description')}
            placeholder='Enter description'
            isDisabled={false}
            field='textArea'
            autoHeight={true}
            styleWrapper={{ gridTemplateColumns: '200px 1fr' }}
          />

          <Field
            label='API Key (optional)'
            classNames={['transparent']}
            style={{ width: '100%' }}
            value={formData.api_key}
            onChange={onChangeValue('api_key')}
            placeholder='Enter API key'
            isDisabled={false}
            field='textField'
            styleWrapper={{ gridTemplateColumns: '200px 1fr' }}
          />

          <Field
            label='Active'
            classNames={['transparent']}
            style={{ width: '100%' }}
            value={formData.is_active}
            onChange={onChangeValue('is_active')}
            isDisabled={false}
            field='checkbox'
            styleWrapper={{ gridTemplateColumns: '200px 1fr' }}
          />
        </div>
      </div>
      <div className={styles.line} />
      <div className={styles.buttons}>
        <button className={styles.buttonLeft} onClick={onClose} disabled={isLoading}>
          Cancel
        </button>
        <button className={styles.buttonRight} onClick={handleSubmit} disabled={isLoading}>
          Create Form
        </button>
      </div>
    </div>
  )
}
